import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, IconButton, Link, Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import FileInputStyles from './FileInput.style';
import { UploadFileInputV3Props } from './FileInput.type';
import useFileInput from './hooks/useFileInput';

const FileInputV3 = ({
  inputName = 'invoice',
  title,
  subTitle,
  classes,
  wrapperClasses,
  documents,
  previewDocuments,
  error,
  example,
  isSingleFile = false,
  onChange,
  onDropChange,
  onDelete,
  onExampleClick,
}: UploadFileInputV3Props) => {
  const { t } = useTranslation();
  const {
    handleChange,
    handleClick,
    handleDeleteFile,
    handleDragOver,
    handleDrop,
    truncateString,
    fileInputRef,
    isInternalError,
  } = useFileInput({
    onChange,
    onDropChange,
    onDelete,
    inputName,
  });

  return (
    <div
      className={clsx(FileInputStyles.wrapper, wrapperClasses)}
      data-testid="file-input-wrapper"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {title ? (
        <div className={FileInputStyles.titleWrapper}>
          <Button
            id={title}
            data-testid="file-input-button"
            type="button"
            variant="text"
            className={clsx(FileInputStyles.button, classes)}
            onClick={handleClick}
          >
            <Typography component="p" sx={{ textAlign: 'left' }}>
              <Typography component="span">{title}</Typography>
              <br />
              {subTitle && typeof subTitle === 'string' && (
                <Typography
                  component="span"
                  sx={{ fontSize: '0.75rem', lineHeight: '1' }}
                >
                  {subTitle}
                </Typography>
              )}
              {subTitle && typeof subTitle !== 'string' && subTitle}
            </Typography>
          </Button>
          {example && (
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline', mt: 1 }}
              onClick={() => onExampleClick?.(example)}
            >
              {t(example.text)}
            </Typography>
          )}
        </div>
      ) : null}
      {documents?.length
        ? documents.map((document) => {
            const fullName =
              document.document.location?.split('/').slice(-1)[0] || '';
            return (
              <Link
                id={document.document.version}
                key={document.document.version}
                href={`${document.document.location}`}
                variant="button"
                className={clsx(FileInputStyles.documentsRow)}
                target="_blank"
              >
                <UploadFileIcon />
                <Typography variant="body2">
                  {truncateString(fullName.split('?')[0])}
                </Typography>
                <CheckCircleIcon className="checkIcon" />
                {onDelete ? (
                  <IconButton
                    data-testid="deleteButton"
                    className="deleteButton"
                    onClick={(e) => handleDeleteFile(e, document)}
                  >
                    <DeleteIcon />
                  </IconButton>
                ) : null}
              </Link>
            );
          })
        : null}
      {previewDocuments?.length
        ? previewDocuments.map((document) => (
            <Link
              id={document.id.toString()}
              key={document.id.toString()}
              href={`#${document.id.toString()}`}
              variant="button"
              className={clsx(FileInputStyles.documentsRow)}
            >
              <UploadFileIcon />
              <Typography variant="body2">
                {truncateString(document.name.split('?')[0])}
              </Typography>
              <CheckCircleIcon className="checkIcon" />
            </Link>
          ))
        : null}
      <input
        id={title?.split(' ').join('_')}
        aria-label="upload file"
        ref={fileInputRef}
        type="file"
        name={inputName}
        multiple
        accept=".pdf,.png,.jpg,.doc,.docx,.xls,.xlsx,.csv,.jpeg,.cer,.bmp,.tif,.tiff"
        onChange={handleChange}
        hidden
      />
      <div
        className={clsx(
          isSingleFile && documents?.length ? FileInputStyles.singleFile : '',
          error || isInternalError.isError
            ? FileInputStyles.errorInputWrapper
            : FileInputStyles.inputWrapper
        )}
      >
        <Button
          id={title ?? 'file-input-button'}
          type="button"
          variant="text"
          className={FileInputStyles.inputButton}
          onClick={handleClick}
          startIcon={<UploadFileIcon />}
        >
          <div className="textWrapper">
            <Typography variant="caption" className="header">
              {t('inpuitFilePlaceholderHeader')}
            </Typography>
            <Typography variant="caption">
              {t('inpuitFilePlaceholder')}
            </Typography>
          </div>
        </Button>
      </div>
      {isSingleFile && documents?.length ? (
        <Typography
          variant="caption"
          color="var(--colors-warning-900)"
          sx={{ mt: 1 }}
        >
          {t('isSingleFileInput')}
        </Typography>
      ) : null}
      {error || isInternalError.isError ? (
        <Typography className={FileInputStyles.errorMessage} variant="caption">
          {error?.message ||
            isInternalError.errorMessage ||
            t('errorRetryMessage')}
        </Typography>
      ) : null}
    </div>
  );
};

export default FileInputV3;
